import { Modal, Box, Button, Alert, TextField } from "@mui/material";
import React, { useState } from "react";

import { modalStyle } from "../constants";
import { sendPostRequest } from "../../utils";
import CopyText from "./CopyText";
import {
  LANGUAGES,
  LINK_PLACEHOLDER,
  LINK_PLACEHOLDER_PATTERN,
  RESUBSCRIBE_TEMPLATES,
  buildTemplate,
} from "./resubscribeTemplates";

const MAX_LENGTH = 1000;

const ResubscribeInviteModal = ({ open, handleClose, user }) => {
  const firstName = (user.name || "").trim().split(/\s+/)[0];

  const [language, setLanguage] = useState(LANGUAGES[0].value);
  const [template, setTemplate] = useState(RESUBSCRIBE_TEMPLATES[0].value);
  const [message, setMessage] = useState(
    buildTemplate(RESUBSCRIBE_TEMPLATES[0].value, LANGUAGES[0].value, firstName)
  );
  const [error, setError] = useState("");
  const [link, setLink] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Choosing a template or a language replaces the text, including any edits
  const doSelectTemplate = (value) => {
    setTemplate(value);
    setMessage(buildTemplate(value, language, firstName));
  };

  const doSelectLanguage = (value) => {
    setLanguage(value);
    setMessage(buildTemplate(template, value, firstName));
  };

  const doSend = () => {
    setIsSubmitting(true);
    setError("");
    sendPostRequest("send-resubscribe-invite", { id: user.id, message })
      .then((res) => res.json())
      .then((data) => {
        setIsSubmitting(false);
        if (data.errMessage) setError(data.errMessage);
        if (data.link) setLink(data.link);
      })
      .catch(() => {
        setIsSubmitting(false);
        setError("Failed to send the invite. Please try again.");
      });
  };

  return (
    <Modal open={open} onClose={handleClose} className="relative">
      <Box sx={{ ...modalStyle, width: 560, maxWidth: "95vw" }}>
        <p className="text-lg font-semibold">Send resubscribe invite</p>
        <p className="text-sm text-gray-600">
          SMS to <b>{user.name}</b> ({user.phone_number})
        </p>

        {error ? (
          <div className="my-4">
            <Alert severity="error">{error}</Alert>
          </div>
        ) : null}

        {link ? (
          <>
            <div className="my-4">
              <Alert severity="success">
                Invite sent. The link is valid for 7 days.
              </Alert>
            </div>
            <p className="text-sm text-gray-600">
              Personal link, for example to paste into an email:
            </p>
            <div className="text-sm break-all">
              <CopyText text={link} />
            </div>
            <div className="flex justify-center mt-6">
              <Button type="button" variant="contained" onClick={handleClose}>
                Close
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="my-4 flex flex-wrap gap-x-6 gap-y-2">
              <div>
                <label htmlFor="invite-language" className="mr-1">
                  Language:
                </label>
                <select
                  id="invite-language"
                  className="border p-1 rounded-md border-gray-400"
                  value={language}
                  onChange={(e) => doSelectLanguage(e.target.value)}
                >
                  {LANGUAGES.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="invite-template" className="mr-1">
                  Template:
                </label>
                <select
                  id="invite-template"
                  className="border p-1 rounded-md border-gray-400"
                  value={template}
                  onChange={(e) => doSelectTemplate(e.target.value)}
                >
                  {RESUBSCRIBE_TEMPLATES.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label[language]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <TextField
              fullWidth
              multiline
              minRows={5}
              label="Message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              inputProps={{ maxLength: MAX_LENGTH }}
            />
            <p className="mt-1 text-xs text-gray-600">
              {message.length} / {MAX_LENGTH} characters.{" "}
              {LINK_PLACEHOLDER_PATTERN.test(message)
                ? `${LINK_PLACEHOLDER} is replaced by the member's personal link.`
                : `No ${LINK_PLACEHOLDER} in the text: the link will be added at the end.`}
            </p>

            <div className="flex justify-center mt-6">
              <Button
                type="button"
                variant="contained"
                onClick={doSend}
                disabled={isSubmitting || !message.trim()}
              >
                {isSubmitting ? "Sending..." : "Send SMS"}
              </Button>
              <span className="ml-2">
                <Button
                  type="button"
                  variant="outlined"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              </span>
            </div>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default ResubscribeInviteModal;
